import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, List, LayoutGrid, Calendar, User, LogOut, BarChart3, Home, Users, Settings, Menu, X } from 'lucide-react';
import './Header.css';

export default function Header({ onAdd, search, onSearch, view, onViewChange, currentUser, onLogout, onShowProgress, onShowProfile, mobileScreen, onMobileScreenChange }) {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const profileRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close menus on outside click / Escape
  useEffect(() => {
    if (!showUserMenu && !showMobileMenu) return;
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    const handleKey = (e) => { 
      if (e.key === 'Escape') {
        setShowUserMenu(false);
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [showUserMenu, showMobileMenu]);
  return (
    <>
      <header className="header">
      <div className="header-inner">
        <div className="header-brand">
          <span className="brand-mark">✦</span>
          <h1 className="brand-name">MyActivities</h1>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Toggle menu"
        >
          {showMobileMenu ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Desktop Controls */}
        <div className="header-controls desktop-only">
          <div className="search-wrap">
            <Search size={15} className="search-icon" />
            <input
              type="text"
              placeholder="Search activities…"
              value={search}
              onChange={e => onSearch(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="view-toggle">
            <button
              className={`view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => onViewChange('list')}
              title="List view"
            >
              <List size={16} />
            </button>
            <button
              className={`view-btn ${view === 'board' ? 'active' : ''}`}
              onClick={() => onViewChange('board')}
              title="Board view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`view-btn ${view === 'calendar' ? 'active' : ''}`}
              onClick={() => onViewChange('calendar')}
              title="Calendar view"
            >
              <Calendar size={16} />
            </button>
          </div>

          {onShowProgress && (
            <button
              className="progress-btn"
              onClick={onShowProgress}
              title="View Progress"
              aria-label="View progress"
            >
              <BarChart3 size={18} />
            </button>
          )}
          <button className="add-btn" onClick={onAdd}>
            <Plus size={16} />
            <span>Add Activity</span>
          </button>

          <div className="user-profile" ref={profileRef}>
            <button
              className="profile-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-haspopup="menu"
              aria-expanded={showUserMenu}
              aria-label={`Account menu for ${currentUser.name}`}
              title={currentUser.name}
            >
              <User size={18} />
              <span className="user-name">{currentUser.name}</span>
            </button>

            {showUserMenu && (
              <div className="user-menu">
                <div className="user-info">
                  <p className="user-email">{currentUser.email}</p>
                  <p className="user-role">{currentUser.role === 'admin' ? 'Administrator' : 'User'}</p>
                </div>
                {onShowProfile && (
                  <button 
                    className="profile-menu-btn" 
                    onClick={() => {
                      onShowProfile();
                      setShowUserMenu(false);
                    }}
                  >
                    <Settings size={16} />
                    <span>Profile</span>
                  </button>
                )}
                <button className="logout-btn" onClick={onLogout}>
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="mobile-menu" ref={mobileMenuRef}>
          <div className="mobile-menu-content">
            {/* Mobile Search */}
            <div className="mobile-search">
              <Search size={16} className="search-icon" />
              <input
                type="text"
                placeholder="Search activities…"
                value={search}
                onChange={e => onSearch(e.target.value)}
                className="mobile-search-input"
              />
            </div>

            {/* Mobile View Toggle */}
            <div className="mobile-view-toggle">
              <button
                className={`mobile-view-btn ${view === 'list' ? 'active' : ''}`}
                onClick={() => {
                  onViewChange('list');
                  setShowMobileMenu(false);
                }}
              >
                <List size={20} />
                <span>List</span>
              </button>
              <button
                className={`mobile-view-btn ${view === 'board' ? 'active' : ''}`}
                onClick={() => {
                  onViewChange('board');
                  setShowMobileMenu(false);
                }}
              >
                <LayoutGrid size={20} />
                <span>Board</span>
              </button>
              <button
                className={`mobile-view-btn ${view === 'calendar' ? 'active' : ''}`}
                onClick={() => {
                  onViewChange('calendar');
                  setShowMobileMenu(false);
                }}
              >
                <Calendar size={20} />
                <span>Calendar</span>
              </button>
            </div>

            {/* Mobile Actions */}
            {onShowProgress && (
              <button
                className="mobile-action-btn"
                onClick={() => {
                  onShowProgress();
                  setShowMobileMenu(false);
                }}
              >
                <BarChart3 size={20} />
                <span>View Progress</span>
              </button>
            )}
            
            <button
              className="mobile-action-btn mobile-add-btn"
              onClick={() => {
                onAdd();
                setShowMobileMenu(false);
              }}
            >
              <Plus size={20} />
              <span>Add Activity</span>
            </button>

            {/* Mobile User Menu */}
            <div className="mobile-user-section">
              <div className="mobile-user-info">
                <User size={20} />
                <div>
                  <p className="mobile-user-name">{currentUser.name}</p>
                  <p className="mobile-user-email">{currentUser.email}</p>
                  <p className="mobile-user-role">{currentUser.role === 'admin' ? 'Administrator' : 'User'}</p>
                </div>
              </div>
              
              {onShowProfile && (
                <button
                  className="mobile-action-btn"
                  onClick={() => {
                    onShowProfile();
                    setShowMobileMenu(false);
                  }}
                >
                  <Settings size={20} />
                  <span>Profile</span>
                </button>
              )}
              
              <button
                className="mobile-action-btn mobile-logout-btn"
                onClick={() => {
                  onLogout();
                  setShowMobileMenu(false);
                }}
              >
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </header>

    {/* Mobile Bottom Navigation */}
    <nav className="mobile-nav">
      <button 
        className={`mobile-nav-item ${mobileScreen === 'overview' ? 'active' : ''}`}
        onClick={() => onMobileScreenChange && onMobileScreenChange('overview')}
      >
        <Home size={20} />
        <span>Overview</span>
      </button>
      <button 
        className={`mobile-nav-item ${mobileScreen === 'chores' ? 'active' : ''}`}
        onClick={() => onMobileScreenChange && onMobileScreenChange('chores')}
      >
        <List size={20} />
        <span>Chores</span>
      </button>
      <button 
        className={`mobile-nav-item ${mobileScreen === 'users' ? 'active' : ''}`}
        onClick={() => onMobileScreenChange && onMobileScreenChange('users')}
      >
        <Users size={20} />
        <span>Users</span>
      </button>
    </nav>
    </>
  );
}
